import { useEffect, useRef } from 'react';

/**
 * Run `fn` on an interval while `enabled` is true.
 *
 * The portal has no push channel, so nothing in it ever learns that a seller
 * submitted a listing or a user replied to support — an admin sitting on a
 * screen sees whatever was there when it loaded. Anywhere someone is waiting
 * on the other side has to ask.
 *
 * `fn` is held in a ref so an inline function doesn't restart the timer on
 * every render, and a run that throws is swallowed — a failed poll should
 * leave the last good data on screen rather than blank the table.
 *
 * Pauses while the tab is hidden, and catches up the moment it comes back, so
 * a portal left open in a background tab costs nothing.
 */
export function usePoll(fn, intervalMs, enabled = true) {
    const fnRef = useRef(fn);
    fnRef.current = fn;

    useEffect(() => {
        if (!enabled || !intervalMs) return undefined;

        let cancelled = false;
        const run = async () => {
            if (cancelled || document.visibilityState !== 'visible') return;
            try {
                await fnRef.current?.();
            } catch {
                /* keep the last good data */
            }
        };

        const id = setInterval(run, intervalMs);
        const onVisible = () => {
            if (document.visibilityState === 'visible') run();
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [intervalMs, enabled]);
}
